import CryptoJS from 'crypto-js'
import axios from 'axios'
import { encryptData } from './encrypt'

interface SignedUrlOptions {
  baseUrl: string // 기본 URL, 예: 'https://cdn.example.com'
  key: string // Base64 인코딩된 비밀 키
  keyName: string
  expirationTime: Date
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace CloudFile {
  const config = {
    EncryptKey: '',
    uploadURL: '',
    key: '',
    keyName: '',
    baseUrl: '',
  }

  // signed URL 캐시 (만료 5분 전까지 재사용)
  const signedUrlCache = new Map<string, { url: string; expiresAt: number }>()
  const CACHE_MARGIN = 5 * 60 * 1000
  function getOptions(): SignedUrlOptions {
    return {
      baseUrl: config.baseUrl,
      key: config.key,
      keyName: config.keyName,
      expirationTime: new Date(Date.now() + 3 * 60 * 60 * 1000),
    }
  }

  // Filepath 는 bucket내부의 파일 경로를 의미합니다.
  // 예 ) a/b a폴더 안 의 b
  // 예 ) a/b/c a폴더 안의 b폴더 안의 c
  // 예 ) /a/b "" 폴더 안의 a 폴더 안의 b
  // 즉 맨 처음에 구분자를 사용하면 안되며, filepath에 확장자도 포함되어야 합니다.

  // 업로드가 완료되는 경우 storage://${bucket}/${filepath} 규칙으로 반환합니다.
  // DB에 해당 url로 저장하면 됩니다.
  export function setEncryptKey(key: string): void {
    config.EncryptKey = key
  }
  export function setUploadURL(uploadURL: string): void {
    config.uploadURL = uploadURL
  }
  export function setConfig(keyName: string, key: string, baseUrl: string): void {
    config.key = key
    config.keyName = keyName
    config.baseUrl = baseUrl
  }

  export async function uploadFile(bucket: string, filepath: string, file: File): Promise<string> {
    const formData = new FormData()
    formData.append('file', file)
    try {
      const { data } = await axios.post(config.uploadURL, formData, {
        headers: getAuthHeader(bucket, filepath),
      })
      if (data.code != 0 && data.code != '0') {
        throw new Error(data.message ?? '업로드 실패')
      }

      return `storage://${bucket}/${filepath}`
    } catch (ex) {
      console.error(ex)
      if (ex instanceof Error) {
        throw ex
      } else {
        throw new Error('업로드 실패')
      }
    }
  }

  export async function uploadFileUrl(url: string, file: File): Promise<string> {
    if (!url.trim().startsWith('storage://') && !url.trim().startsWith('local-storage://')) {
      return ''
    }
    const path = url.trim().replace('local-storage://', '').replace('storage://', '').split('/')
    const bucket = path.shift() || ''
    const filepath = path.join('/')
    return await uploadFile(bucket, filepath, file)
  }

  /**
   * 응답 본문을 조각 단위로 읽으며 진행률(0~100)을 알린다.
   * Content-Length 가 없거나 스트림을 못 읽는 환경이면 진행률 없이 통째로 받는다.
   */
  async function readBlobWithProgress(
    response: Response,
    onProgress: (percent: number) => void,
  ): Promise<Blob> {
    const total = Number(response.headers.get('content-length')) || 0
    const reader = response.body?.getReader()
    if (!reader || total <= 0) return await response.blob()

    const chunks: BlobPart[] = []
    let received = 0
    let lastPercent = -1
    for (;;) {
      const { done, value } = await reader.read()
      if (done) break
      if (!value) continue
      chunks.push(value)
      received += value.byteLength
      const percent = Math.min(100, Math.floor((received / total) * 100))
      if (percent !== lastPercent) {
        lastPercent = percent
        onProgress(percent)
      }
    }
    return new Blob(chunks, { type: response.headers.get('content-type') || '' })
  }

  export async function downloadFile(
    bucket: 'homecheck-service' | string,
    filepath: string,
    onProgress?: (percent: number) => void,
  ): Promise<File | undefined> {
    let downloadURL = ''
    switch (bucket) {
      case 'homecheck-service':
        downloadURL = signUrl(filepath)
        break
      default:
        break
    }
    if (!downloadURL) return
    try {
      const response = await fetch(downloadURL)
      if (!response.ok) return undefined
      const blob = onProgress
        ? await readBlobWithProgress(response, onProgress)
        : await response.blob()
      return blobToFile(blob, filepath.split('/').pop() || '')
    } catch (ex) {
      console.error(ex)
      return undefined
    }
  }

  export async function downloadFileFromResourceUrl(
    url: string,
    onProgress?: (percent: number) => void,
  ): Promise<File | undefined> {
    if (!url.trim().startsWith('storage://')) {
      return
    }
    const path = url.trim().replace('storage://', '').split('/')
    const bucket = path.shift() || ''
    const filepath = path.join('/')
    return await downloadFile(bucket, filepath, onProgress)
  }

  export async function getBase64FromResourceUrl(url: string): Promise<string> {
    if (!url.trim().startsWith('storage://')) {
      return ''
    }
    const path = url.trim().replace('storage://', '').split('/')
    const bucket = path.shift() || ''
    const filepath = path.join('/')

    let downloadURL = ''
    switch (bucket) {
      case 'homecheck-service':
        downloadURL = signUrl(filepath)
        break
      default:
        break
    }
    if (!downloadURL) return ''
    try {
      const response = await fetch(downloadURL)
      if (!response.ok) return ''
      const blob = await response.blob()
      const reader = new FileReader()
      reader.readAsDataURL(blob)
      return new Promise(resolve => {
        reader.onloadend = (): void => {
          resolve(reader.result as string)
        }
      })
    } catch (ex) {
      console.error(ex)
      return ''
    }
  }

  export function ResourceUrlToSignedUrl(url: string): string {
    if (!url.trim().startsWith('storage://')) {
      return ''
    }

    // 캐시 히트 시 재사용
    const cached = signedUrlCache.get(url)
    if (cached && cached.expiresAt > Date.now() + CACHE_MARGIN) {
      return cached.url
    }

    const path = url.trim().replace('storage://', '').split('/')
    const bucket = path.shift() || ''
    const filepath = path.join('/')

    let downloadURL = ''
    switch (bucket) {
      case 'homecheck-service':
        downloadURL = signUrl(filepath)
        break
      default:
        break
    }

    if (downloadURL) {
      signedUrlCache.set(url, {
        url: downloadURL,
        expiresAt: Date.now() + 3 * 60 * 60 * 1000,
      })
    }

    return downloadURL
  }

  // 서명된 URL 생성 함수
  export function signUrl(filePath: string): string {
    const { baseUrl, key, keyName, expirationTime } = getOptions()
    const FilePath = encodeURI(filePath)
    const unixTime = Math.floor(expirationTime.getTime() / 1000)
    const urlToSign = `${baseUrl}${FilePath}?Expires=${unixTime}&KeyName=${keyName}`

    const encoded = getSignature(key, urlToSign)
    return `${urlToSign}&Signature=${encoded}`
  }

  function getAuthHeader(
    bucket: string,
    filepath: string,
  ): { authorization: string; accesskey: string } {
    return {
      authorization: `${getRandomString(10)}${encryptData(bucket, config.EncryptKey)}`,
      accesskey: `${getRandomString(10)}${encryptData(filepath, config.EncryptKey)}`,
    }
  }
}

// GCP로 파일 업로드 및 다운로드 방법을 제공하는 모듈
function getRandomString(length: number): string {
  const charsNumber = '0123456789'
  const charsLower = 'abcdefghijklmnopqrstuvwxyz'
  const charsUpper = 'ABCDEFGHIJKLMNOPQRSTUVWXTZ'
  const charsAll = [charsNumber, charsLower, charsUpper] //  Include special character by default but allow checkbox to toggle option
  const chars = charsAll.join('')
  let randomString = ''
  for (let i = 0; i < length; i++) {
    // Get string length
    const randNum = Math.floor(Math.random() * chars.length) // and then
    randomString += chars.substring(randNum, randNum + 1) // randomize it
  }

  return randomString
}

function blobToFile(blob: Blob, fileName: string): File {
  // Blob의 type과 lastModified 값을 사용해서 File 객체를 생성합니다.
  const file = new File([blob], fileName, { type: blob.type, lastModified: Date.now() })
  return file
}

// 서명 생성 함수
function getSignature(key: string, input: string): string {
  const privateKey = CryptoJS.enc.Base64.parse(key)
  const signature = CryptoJS.HmacSHA1(input, privateKey)
  return signature
    .toString(CryptoJS.enc.Base64)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
}
